import { Drawer, Group, Stack, Text, Title } from "@mantine/core"
import { CancelButton, SubmitButton } from "@components/Buttons"
import { SuspenseWithErrorBoundary } from "@components/suspense"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { RenderActivity, SharedControls } from "./shared-modal-fields"
import { useTranslation } from "react-i18next"
import { DefaultError } from "@components/error-context"
import {
  AddCastRequest,
  EditCastRequest,
} from "@/@client/Theasoft_ApiServer_Orchestra"
import { UseFormReturnType } from "@mantine/form"
import {
  hasValidationErrors,
  tryGetValidationErrorsAsMantineErrors,
} from "@/utils/to-mantine-validation-errors"
import { useSuspenseQueryGet } from "@/typed-client/hooks/use-suspense-query-get"
import { Form } from "@components/typed-form"

export type ActivityCellCoordinate = {
  type: "activity-cell"
  contractId: number
  activityId: number
  // personId: number // maybe remove
  workareaId: number
}

export function AddCastModal(props: {
  departmentId: number
  coordinate: null | ActivityCellCoordinate
  onAdded: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation("app_server")
  return (
    <Drawer
      position="right"
      overlayProps={{ backgroundOpacity: 0.2, blur: 0 }}
      opened={props.coordinate !== null}
      onClose={() => props.onCancel()}
      title={<Title order={3}>{t("CastInstrument")}</Title>}
    >
      <SuspenseWithErrorBoundary>
        {props.coordinate !== null ? (
          <AddContent
            departmentId={props.departmentId}
            coordinate={props.coordinate}
            onAdded={props.onAdded}
            onCancel={props.onCancel}
          />
        ) : null}
      </SuspenseWithErrorBoundary>
    </Drawer>
  )
}

function AddContent({
  departmentId,
  coordinate,
  onAdded,
  onCancel,
}: {
  departmentId: number
  coordinate: ActivityCellCoordinate
  onAdded: () => void
  onCancel: () => void
}) {
  const { t } = useTranslation("app_server")
  const { data: activity } = useSuspenseQueryGet(
    "/api/Orchestra/GetOrchestraActivity",
    {
      activityId: coordinate.activityId,
      departmentId,
    }
  )
  const { data: initialFormFields } = useSuspenseQueryGet(
    "/api/Orchestra/GetInitialCastFormFields",
    {
      activityId: coordinate.activityId,
      contractId: coordinate.contractId,
      workareaId: coordinate.workareaId,
    }
  )
  return (
    <Form
      url="/api/Orchestra/AddCast"
      onSuccess={() => onAdded()}
      onError={(e, _, __, f) => {
        if (e instanceof DefaultError && hasValidationErrors(e)) {
          const validationErrors = tryGetValidationErrorsAsMantineErrors(e, t)
          if (validationErrors) {
            f.setErrors(validationErrors)
          }
        }
        showErrorNotification(e)
      }}
      initialValues={initialFormFields}
      transform={(v) => v}
    >
      {(f, _, m) => {
        const errors = Object.keys(f.errors).map((k) => f.errors[k])
        return (
          <Stack>
            <RenderActivity activity={activity} />
            <Text fw={700}>
              {/* TODO: get person name for contract id? */}
              {/* or from context */}
              {/* <PersonName id={coordinate.personId} /> */}
            </Text>
            <SharedControls
              // its difficult to satisfy typescript in this case
              f={f as UseFormReturnType<AddCastRequest | EditCastRequest>}
              _={_.properties}
              activityId={coordinate.activityId}
              contractId={coordinate.contractId}
              workareaId={coordinate.workareaId}
            />
            <Group justify="space-between" mt="md">
              <CancelButton onClick={onCancel} />
              <SubmitButton disabled={errors.length > 0} loading={m.isPending}>
                {t("CastVerb")}
              </SubmitButton>
            </Group>
          </Stack>
        )
      }}
    </Form>
  )
}
